import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Zap, Clock, Wrench, FileText, CheckCircle2, AlertTriangle, Scale, ArrowDown } from "lucide-react";
import { Section } from "@/components/common";
import PowerStudyOrderForm from "@/components/PowerStudyOrderForm";
import SampleReportViewer from "@/components/SampleReportViewer";

export const DISCLAIMER = "This is an independent pre-screen estimate, not a utility load study or interconnection approval. Final capacity is determined only by the utility.";

const DELIVERABLES = [
  "Site facts and location map",
  "Every power provider serving or near the site, with territory boundaries",
  "Side-by-side provider comparison: available capacity, timeline, upgrade needs, and rates where public",
  "Nearest substations and transmission lines with voltage and distance",
  "Power estimates in tiers: available today, with distribution upgrades, with new transmission",
  "Estimated timeline to energization for each tier",
  "Known obstacles: moratoriums, zoning, queue backlogs, water limits",
  "Feasibility verdict",
  "Contact list with names, titles, and phone numbers for each utility, the county, and economic development",
  "Recommended next steps, including how to request a formal load study from each utility",
  "Sourced citations for every finding",
];

const ANSWERS = [
  [Zap, "How much power", "A realistic estimate of the capacity your site can get, not a best-case guess."],
  [Clock, "How soon", "Estimated time to energization for each capacity tier."],
  [Wrench, "What upgrades", "Distribution and transmission upgrades the site may need to get there."],
];

const PROVIDERS = ["Investor-owned utility", "Electric co-op", "Municipal utility", "Transmission owner"];

const Disclaimer = () => (
  <div data-testid="power-study-disclaimer" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 flex gap-3">
    <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
    <p className="text-amber-100 text-sm leading-relaxed"><strong>Disclaimer:</strong> {DISCLAIMER}</p>
  </div>
);

const Hero = () => (
  <div className="grid-bg border-b border-white/10">
    <Section className="py-16 sm:py-24">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <p className="eyebrow mb-3">DYOR Power Feasibility Study</p>
        <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.05]">
          The next best thing to a <span className="text-[#84CC16]">utility load study.</span>
        </h1>
        <p className="text-slate-300 text-base sm:text-lg mt-6 max-w-2xl leading-relaxed">
          We estimate how much power a site can realistically get, how soon, and what upgrades it may need, using public utility
          filings, transmission and substation data, and comparable sites. It costs about a third of a traditional study.
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-8">
          <a href="#order" data-testid="power-study-hero-order"
            className="inline-flex items-center gap-2 bg-[#84CC16] hover:bg-[#9ee325] text-[#0B0F17] font-bold px-6 py-3.5 rounded-full transition-colors glow-lime">
            Order a Study <ArrowDown size={17} />
          </a>
          <a href="#what-you-get" data-testid="power-study-sample-link"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-[#84CC16] text-white font-semibold px-6 py-3.5 rounded-full transition-colors">
            <FileText size={16} /> See a sample report
          </a>
          <p className="text-slate-300 text-sm" data-testid="power-study-price">
            <span className="text-white font-bold text-lg">$5,000</span> · $1,500 deposit today, $3,500 when your report is ready
          </p>
        </div>
      </motion.div>
    </Section>
  </div>
);

const Explainer = () => (
  <Section className="py-14 sm:py-20">
    <div className="grid sm:grid-cols-3 gap-4">
      {ANSWERS.map(([Icon, t, d]) => (
        <div key={t} className="card p-6" data-testid={`power-answer-${t}`}>
          <Icon className="text-[#3B82F6] mb-3" size={22} />
          <h3 className="font-display font-semibold text-lg text-white">{t}</h3>
          <p className="text-slate-400 text-sm mt-1.5">{d}</p>
        </div>
      ))}
    </div>
    <div className="grid lg:grid-cols-2 gap-6 mt-10">
      <div className="card p-6 sm:p-8">
        <FileText className="text-[#84CC16] mb-3" size={22} />
        <h2 className="font-display font-bold text-xl text-white">As much as possible for your money</h2>
        <p className="text-slate-400 mt-2 leading-relaxed text-sm sm:text-base">
          This is not a formal load study, so we give you as much information as we can for what you pay. You get every number,
          map, contact, and citation we find, so you can make a confident go/no-go decision before spending on a utility study.
        </p>
      </div>
      <div className="card p-6 sm:p-8">
        <Scale className="text-[#84CC16] mb-3" size={22} />
        <h2 className="font-display font-bold text-xl text-white">Every provider, side by side</h2>
        <p className="text-slate-400 mt-2 leading-relaxed text-sm sm:text-base">
          If more than one power provider serves or could serve the site, the report covers every one of them and compares them side by side.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {PROVIDERS.map((p) => <span key={p} className="text-xs font-mono px-2.5 py-1 rounded-full border border-white/10 text-slate-300">{p}</span>)}
        </div>
      </div>
    </div>
  </Section>
);

const Included = () => (
  <Section className="pb-14 sm:pb-20">
    <p className="eyebrow mb-2">What's included</p>
    <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-8">Deliverables in every study</h2>
    <ul className="grid md:grid-cols-2 gap-3" data-testid="power-study-deliverables">
      {DELIVERABLES.map((d, i) => (
        <li key={d} data-testid={`deliverable-${i}`} className="flex items-start gap-3 card px-5 py-4">
          <CheckCircle2 size={18} className="text-[#84CC16] shrink-0 mt-0.5" />
          <span className="text-slate-200 text-sm sm:text-base">{d}</span>
        </li>
      ))}
    </ul>
    <p className="text-slate-400 text-sm mt-6">
      Every completed study is delivered as three files: the full report, a one-page executive summary, and the multi-provider comparison table.{" "}
      <Link to="/power-study/sample" data-testid="deliverables-sample-link" className="text-[#3B82F6] underline">View a redacted sample</Link>
    </p>
    <div className="mt-6"><Disclaimer /></div>
  </Section>
);

export default function PowerStudy() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#order") setTimeout(() => document.getElementById("order")?.scrollIntoView({ behavior: "smooth" }), 100);
  }, [hash]);
  return (
    <div>
      <Hero />
      <Explainer />
      <Included />
      <Section className="pb-14 sm:pb-20">
        <div id="what-you-get" className="scroll-mt-24" data-testid="what-you-get-section">
          <p className="eyebrow mb-2">What you get</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-2">See a completed study</h2>
          <p className="text-slate-400 text-sm sm:text-base mb-6 max-w-2xl">Every order includes all three documents: the full report, the one-page executive summary, and the provider comparison.</p>
          <SampleReportViewer />
        </div>
      </Section>
      <Section className="pb-10">
        <div id="order" className="scroll-mt-24"><PowerStudyOrderForm disclaimer={DISCLAIMER} /></div>
      </Section>
    </div>
  );
}
